/**
 * ChartHeader — chart cockpit header with Python-v3-style timeframe pills.
 *
 * Two horizontal pill rows mirror the v3 layout:
 *   PERIOD:    1D  5D  1M  3M  6M  YTD  1Y  2Y  5Y  MAX
 *   INTERVAL:  1s  5s  15s  30s | 1m  2m  5m  15m  30m | 1h  2h  4h | D  W  M | CUSTOM
 *
 * The CUSTOM pill opens an input that accepts "<number><unit>" where unit
 * is s/m/h/d/w. The string passes through to /api/ohlcv?interval=...
 * unchanged; the router parses it server-side and maps to the upstream
 * provider's nearest supported timeframe.
 *
 * Stats line below: LAST / CHG / HI / LO / VOL / RSI / ATR / TF.
 */
import { useState, useEffect } from 'react';
import { useStore } from '@/store';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { fetchBars } from '@/lib/bars';
import type { Bar } from '@/types';
import { RSI, ATR } from '@/lib/indicators';
import { ORG, GRN, RED, MUT, FONT } from '@/theme';

// Period pills. Values are sent to the backend as ?period=... and map to
// approximate bar counts via mapPeriodToBarCount() on the router side.
const PERIODS: Array<[string, string]> = [
  ['1d',  '1D'], ['5d',  '5D'],
  ['1mo', '1M'], ['3mo', '3M'], ['6mo', '6M'], ['ytd', 'YTD'],
  ['1y',  '1Y'], ['2y',  '2Y'], ['5y',  '5Y'], ['max', 'MAX'],
];

// Interval pills grouped by cadence — seconds | minutes | hours | daily.
// Sub-minute bars require Polygon; Alpaca free tier resolves to 1Min as
// the floor and the chart shows whatever the router returns.
const INTERVAL_GROUPS: Array<Array<[string, string]>> = [
  [['1s', '1s'], ['5s', '5s'], ['15s', '15s'], ['30s', '30s']],
  [['1m', '1m'], ['2m', '2m'], ['5m', '5m'], ['15m', '15m'], ['30m', '30m']],
  [['1h', '1h'], ['2h', '2h'], ['4h', '4h']],
  [['1d', 'D'],  ['1wk', 'W'], ['1mo', 'M']],
];

function Pill({
  active, onClick, children, dim,
}: { active: boolean; onClick: () => void; children: React.ReactNode; dim?: boolean }) {
  return (
    <button onClick={onClick} style={{
      background:   active ? ORG : 'transparent',
      color:        active ? '#000' : (dim ? '#444' : '#888'),
      border:       '1px solid ' + (active ? ORG : '#222'),
      fontFamily:   FONT,
      fontSize:     10,
      letterSpacing: 0.5,
      padding:      '2px 7px',
      borderRadius: 2,
      cursor:       'pointer',
      fontWeight:   active ? 'bold' : 'normal',
    }}>
      {children}
    </button>
  );
}

function Stat({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <span style={{ marginRight: 14, fontFamily: FONT }}>
      <span style={{ color: MUT, fontSize: 10, marginRight: 4 }}>{label}</span>
      <span style={{ color: color ?? '#e0e0e0', fontWeight: 'bold', fontSize: 12 }}>{value}</span>
    </span>
  );
}

// Mirror of router-side classifySymbol so the header can show STOCK /
// FUTURE / OPTION at a glance without waiting for the response headers.
// This is a display-only heuristic — the router remains the source of
// truth for the actual data dispatch.
const FUT_ROOTS = new Set([
  'ES','MES','NQ','MNQ','YM','MYM','RTY','M2K',
  'ZN','ZB','ZF','ZT','UB','TN',
  'GC','MGC','SI','SIL','HG','MHG','PL','PA',
  'CL','MCL','NG','MNG','RB','HO','BZ',
  'ZC','ZS','ZW','ZM','ZL','ZO','ZR',
  '6E','6B','6J','6A','6C','6S','6N','6M','M6E','M6A','M6B',
  'CC','KC','CT','SB','OJ','LE','GF','HE',
  'BTC','MBT','ETH','MET',
]);
function assetClassOf(sym: string): 'STOCK' | 'FUTURE' | 'OPTION' {
  const s = (sym || '').toUpperCase().replace(/^\//, '');
  if (!s) return 'STOCK';
  if (s.endsWith('=F')) return 'FUTURE';
  if (/^[A-Z]{1,6}\d{6}[CP]\d{8}$/.test(s)) return 'OPTION';
  for (let n = 4; n >= 1; n--) {
    if (s.length < n + 2) continue;
    const root = s.slice(0, n);
    if (!FUT_ROOTS.has(root)) continue;
    if (/^[FGHJKMNQUVXZ]\d{1,2}$/.test(s.slice(n))) return 'FUTURE';
  }
  if (FUT_ROOTS.has(s) && s.length <= 4) return 'FUTURE';
  return 'STOCK';
}
function AssetPill({ kind }: { kind: 'STOCK' | 'FUTURE' | 'OPTION' }) {
  const palette: Record<string, string> = {
    STOCK:  '#8b7355',   // muted amber — equity default
    FUTURE: ORG,          // orange — the differentiator
    OPTION: '#5b6b8a',   // steel — options
  };
  return (
    <span style={{
      fontFamily: FONT, fontSize: 9, letterSpacing: 0.6,
      color: palette[kind], border: `1px solid ${palette[kind]}`,
      padding: '1px 5px', borderRadius: 2, marginLeft: 8,
      textTransform: 'uppercase', fontWeight: 'bold',
    }}>{kind}</span>
  );
}

export function ChartHeader() {
  const focus       = useStore(s => s.focusSymbol);
  const setFocus    = useStore(s => s.setFocus);
  const period      = useStore(s => s.period);
  const setPeriod   = useStore(s => s.setPeriod);
  const interval    = useStore(s => s.interval);
  const setInterval = useStore(s => s.setInterval);
  const [draft, setDraft] = useState(focus);

  // Custom-interval popover state. Open when the CUSTOM pill is active or
  // when the current interval doesn't match any preset (e.g. "47s").
  const allPresetIntervals = INTERVAL_GROUPS.flat().map(([v]) => v);
  const isCustom = !allPresetIntervals.includes(interval);
  const [customOpen, setCustomOpen] = useState(isCustom);
  const [customDraft, setCustomDraft] = useState(isCustom ? interval : '47s');
  useEffect(() => { if (isCustom) setCustomDraft(interval); }, [interval, isCustom]);

  const submitCustom = () => {
    const v = customDraft.trim().toLowerCase();
    if (/^\d+(s|m|h|d|w|mo)$/.test(v)) {
      setInterval(v);
      setCustomOpen(false);
    }
  };

  // Live bars + indicators for the stats line. Reuses the same query
  // key MainChart subscribes to, so React Query dedupes.
  // encodeURIComponent so tokens with `=F` or leading `/` (futures
  // shorthands like ES=F or /ES) reach the router intact. classifySymbol
  // server-side handles normalization + dispatch to Yahoo or Tradovate.
  // Shared fetcher (lib/bars) on the SAME key MainChart uses -- see lib/bars.ts
  // for why both must be identical.
  const { data: bars } = useQuery<Bar[]>({
    queryKey: ['bars', focus, period, interval],
    queryFn:  () => fetchBars(focus, period, interval),
    staleTime:       15_000,
    refetchInterval: 15_000,
    refetchOnWindowFocus: true,
  });

  // Shared quote query — dedupes with the tape's ['tape-quote'] shape so the
  // LAST / CHG stats mirror it. GET /api/quote returns { last, mid, change_pct }
  // (see TickerTape). A previous version declared { price, pctChange } here --
  // fields the endpoint never sends -- so `quote?.price` was always undefined
  // and this whole fallback tier silently did nothing. Match the real shape.
  interface Quote { last?: number; mid?: number; change_pct?: number; }
  const { data: quote } = useQuery<Quote>({
    queryKey:        ['wl-quote', focus],
    queryFn:         () => api(`/api/quote/${encodeURIComponent(focus)}`),
    staleTime:       15_000,
    refetchInterval: 15_000,
    refetchOnWindowFocus: true,
  });

  const liveTick = useStore(s => s.quotes[focus]);
  const last  = bars?.[bars.length - 1];
  const prev  = bars?.[bars.length - 2];
  const barPrice = last && Number.isFinite(last.close) ? last.close : null;
  // Guard against a live price for a DIFFERENT instrument. Bare "ES" is the
  // E-mini future on the chart feed (~7800) but historically resolved to the
  // NYSE equity ES = Eversource (~$72) on the quote/tick feed. The chart's own
  // bars decide which instrument this pane is, so distrust any live price more
  // than 5x off the last bar -- no real quote moves that far intrabar. (The
  // server now routes futures quotes correctly too; this is defense in depth.)
  const sameInstrument = (p: number | null | undefined): p is number =>
    typeof p === "number" && Number.isFinite(p) &&
    (barPrice == null || (p > barPrice * 0.2 && p < barPrice * 5));
  // Prefer the live tick, then the 15s /api/quote poll, then the bar close --
  // each finite- AND instrument-checked so a wrong-scale feed can't override
  // the chart or put NaN on the header.
  const tickLast   = liveTick?.last;
  const tickPrice  = sameInstrument(tickLast) ? tickLast : null;
  const tickPct    = tickPrice != null && Number.isFinite(liveTick?.change_pct) ? liveTick!.change_pct : null;
  const quotePxRaw = quote?.last ?? quote?.mid;
  const quotePrice = sameInstrument(quotePxRaw) ? quotePxRaw : null;
  const quotePct   = quotePrice != null && Number.isFinite(quote?.change_pct) ? (quote!.change_pct as number) : null;
  const livePrice  = tickPrice ?? quotePrice ?? barPrice;
  const barPct     = (last && prev && Number.isFinite(last.close) && Number.isFinite(prev.close) && prev.close !== 0)
    ? ((last.close - prev.close) / prev.close) * 100 : null;
  const livePctRaw = tickPct ?? quotePct ?? barPct;
  // UNKNOWN CHANGE IS NOT ZERO. `?? 0` printed "+0.00%" in GREEN whenever the
  // tick carried no change_pct AND the quote failed AND history was too short --
  // i.e. "flat on the day", a decision input, fabricated from three failures.
  // The RSI/ATR fix three lines below already does this correctly; CHG did not.
  const livePct    = Number.isFinite(livePctRaw as number) ? (livePctRaw as number) : NaN;
  const chg   = (last && prev && Number.isFinite(last.close) && Number.isFinite(prev.close)) ? last.close - prev.close : NaN;
  const chgPc = livePct;
  // NaN, not 0, on short histories. `?? 0` also failed to catch NaN (only
  // null/undefined), so a legitimately-undefined RSI printed as "RSI 0.0"
  // — a maximally-oversold reading that does not exist — and ATR printed
  // "$0.00", which a trader sizing a stop off ATR would act on.
  const rsi14 = bars && bars.length > 14
    ? RSI(bars.map(b => b.close), 14).at(-1) ?? NaN : NaN;
  const atr14 = bars && bars.length > 14
    ? ATR(bars.map(b => b.high), bars.map(b => b.low), bars.map(b => b.close), 14).at(-1) ?? NaN : NaN;
  const isUp = chg >= 0;

  return (
    <div className="pane-hdr" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 4, padding: '6px 10px' }}>
      {/* Row 1: ticker input + GO + live price */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, width: '100%', flexWrap: 'wrap' }}>
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value.toUpperCase())}
          onKeyDown={(e) => { if (e.key === 'Enter') setFocus(draft); }}
          style={{ width: 80, fontSize: 12, padding: '3px 8px', height: 22, border: `1px solid ${ORG}` }}
        />
        <button onClick={() => setFocus(draft)} style={{
          background: ORG, color: '#000', border: 'none',
          fontFamily: FONT, fontSize: 11, fontWeight: 'bold',
          padding: '2px 12px', height: 22, borderRadius: 2,
        }}>GO</button>

        <span style={{
          fontFamily: FONT, fontSize: 14, fontWeight: 'bold',
          color: ORG, marginLeft: 12, letterSpacing: 0.5,
        }}>
          {focus}{livePrice != null ? ` $${livePrice.toFixed(2)}` : ''}
        </span>
        <AssetPill kind={assetClassOf(focus)} />
        <span style={{ fontFamily: FONT, fontSize: 11, marginLeft: 4, color: isUp ? GRN : RED }}>
          {livePrice != null && Number.isFinite(chgPc) ? `${chgPc >= 0 ? '+' : ''}${chgPc.toFixed(2)}%` : ''}
        </span>
      </div>

      {/* Row 2: PERIOD pills */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 3, width: '100%', flexWrap: 'wrap' }}>
        {PERIODS.map(([v, lbl]) => (
          <Pill key={v} active={period === v} onClick={() => setPeriod(v)}>{lbl}</Pill>
        ))}
      </div>

      {/* Row 3: INTERVAL pills (grouped: seconds | minutes | hours | daily | custom) */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 3, width: '100%', flexWrap: 'wrap' }}>
        {INTERVAL_GROUPS.map((group, gi) => (
          <span key={gi} style={{ display: 'flex', gap: 3, marginRight: 6 }}>
            {group.map(([v, lbl]) => (
              <Pill key={v} active={interval === v && !customOpen} onClick={() => { setInterval(v); setCustomOpen(false); }}>
                {lbl}
              </Pill>
            ))}
          </span>
        ))}
        <Pill active={isCustom || customOpen} onClick={() => setCustomOpen(o => !o)}>
          CUSTOM{isCustom ? ` (${interval})` : ''}
        </Pill>
        {customOpen && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <input
              value={customDraft}
              onChange={(e) => setCustomDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') submitCustom(); }}
              placeholder="47s / 3m / 2h"
              style={{ width: 70, fontSize: 10, padding: '2px 5px', height: 20 }}
            />
            <button onClick={submitCustom} style={{
              background: ORG, color: '#000', border: 'none',
              fontFamily: FONT, fontSize: 10, fontWeight: 'bold',
              padding: '2px 8px', borderRadius: 2,
            }}>APPLY</button>
            <span style={{ fontSize: 9, color: MUT }}>
              n + s/m/h/d/w/mo
            </span>
          </div>
        )}
      </div>

      {/* Row 4: stats line — LAST / CHG / HI / LO / VOL / RSI / ATR / TF */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', width: '100%' }}>
        <Stat label="LAST" value={livePrice != null ? `$${livePrice.toFixed(2)}` : '--'} />
        {/* Unknown change renders as "--" and UNCOLOURED: a green +0.00% is a
            claim that the name is flat today, which is a position-sizing input. */}
        <Stat label="CHG"
          value={livePrice != null && Number.isFinite(chgPc) ? `${chgPc >= 0 ? '+' : ''}${chgPc.toFixed(2)}%` : '--'}
          color={Number.isFinite(chgPc) ? (chgPc >= 0 ? GRN : RED) : undefined} />
        {/* These are the LAST BAR's high/low, not the period's. On a 1Y/1D
            chart "HI/LO" reads as the 52-week range to every trader alive;
            showing today's single-bar range under that label is a real
            misread. Labelled for what it is. Finite-guarded: fetchBars only
            filters on time+close, so high/low/volume can still be NaN. */}
        <Stat label="BAR HI" value={last && Number.isFinite(last.high)   ? `$${last.high.toFixed(2)}` : '--'} />
        <Stat label="BAR LO" value={last && Number.isFinite(last.low)    ? `$${last.low.toFixed(2)}`  : '--'} />
        <Stat label="VOL"  value={last && Number.isFinite(last.volume) ? `${(last.volume / 1e6).toFixed(2)}M` : '--'} />
        <Stat label="RSI"  value={Number.isFinite(rsi14) ? rsi14.toFixed(1) : '--'} />
        <Stat label="ATR"  value={Number.isFinite(atr14) ? `$${atr14.toFixed(2)}` : '--'} />
        <Stat label="TF"   value={`${period.toUpperCase()}/${interval}`} />
      </div>
    </div>
  );
}
